import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';

const {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiRefreshCw,
  FiFileText,
  FiSearch,
  FiFilter,
  FiX,
  FiCheck,
  FiEye,
  FiGlobe,
  FiHome,
  FiInfo,
  FiHelpCircle,
  FiShield,
  FiLink,
  FiImage
} = FiIcons;

const ContentManagement = () => {
  const [contentSections, setContentSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showRichTextEditor, setShowRichTextEditor] = useState(false);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm();

  const contentTypes = [
    { value: 'hero', label: 'Hero Section', icon: FiHome, color: 'bg-blue-100 text-blue-800' },
    { value: 'feature', label: 'Feature Highlight', icon: FiInfo, color: 'bg-green-100 text-green-800' },
    { value: 'contact', label: 'Contact Information', icon: FiGlobe, color: 'bg-purple-100 text-purple-800' },
    { value: 'faq', label: 'FAQ', icon: FiHelpCircle, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'terms', label: 'Terms & Conditions', icon: FiShield, color: 'bg-red-100 text-red-800' },
    { value: 'privacy', label: 'Privacy Policy', icon: FiShield, color: 'bg-indigo-100 text-indigo-800' },
    { value: 'footer', label: 'Footer Content', icon: FiLink, color: 'bg-gray-100 text-gray-800' },
    { value: 'service', label: 'Service Page', icon: FiFileText, color: 'bg-orange-100 text-orange-800' }
  ];

  const pageLocations = [
    { value: 'homepage', label: 'Homepage' },
    { value: 'contact', label: 'Contact Page' },
    { value: 'faq', label: 'FAQ Page' },
    { value: 'terms', label: 'Terms Page' },
    { value: 'privacy', label: 'Privacy Page' },
    { value: 'footer', label: 'Footer' },
    { value: 'service', label: 'Service Pages' },
    { value: 'global', label: 'Global' }
  ];

  useEffect(() => {
    fetchContentSections();
  }, []);

  const fetchContentSections = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('content_sections')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContentSections(data || []);
    } catch (error) {
      console.error('Error fetching content sections:', error);
      toast.error('Failed to load content sections');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchContentSections();
      toast.success('Content sections refreshed');
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      const contentData = {
        title: data.title,
        content: data.content,
        content_type: data.content_type,
        page_location: data.page_location,
        display_order: data.display_order || 0,
        is_active: data.is_active || true,
        updated_at: new Date().toISOString()
      };

      if (editingSection) {
        // Update existing section
        const { error } = await supabase
          .from('content_sections')
          .update(contentData)
          .eq('id', editingSection.id);

        if (error) throw error;
        toast.success('Content section updated successfully');
      } else {
        // Create new section
        const { error } = await supabase
          .from('content_sections')
          .insert([{ ...contentData, created_at: new Date().toISOString() }]);

        if (error) throw error;
        toast.success('Content section created successfully');
      }

      handleCloseModal();
      fetchContentSections();
    } catch (error) {
      console.error('Error saving content section:', error);
      toast.error('Failed to save content section');
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setValue('title', section.title);
    setValue('content', section.content);
    setValue('content_type', section.content_type);
    setValue('page_location', section.page_location);
    setValue('display_order', section.display_order);
    setValue('is_active', section.is_active);
    setShowModal(true);
  };

  const handleDelete = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this content section?')) return;

    try {
      const { error } = await supabase
        .from('content_sections')
        .delete()
        .eq('id', sectionId);

      if (error) throw error;
      toast.success('Content section deleted successfully');
      fetchContentSections();
    } catch (error) {
      console.error('Error deleting content section:', error);
      toast.error('Failed to delete content section');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSection(null);
    setShowRichTextEditor(false);
    reset();
  };

  const getContentTypeInfo = (type) => {
    return contentTypes.find(t => t.value === type) || contentTypes[0];
  };

  const getPageLocationInfo = (location) => {
    return pageLocations.find(l => l.value === location) || pageLocations[0];
  };

  const filteredSections = contentSections.filter(section => {
    const matchesSearch = section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         section.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || section.content_type === typeFilter;
    const matchesLocation = locationFilter === 'all' || section.page_location === locationFilter;
    
    return matchesSearch && matchesType && matchesLocation;
  });

  const toggleRichTextEditor = () => {
    setShowRichTextEditor(!showRichTextEditor);
  };

  const insertRichTextTag = (tag) => {
    const textarea = document.querySelector('textarea[name="content"]');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const before = text.substring(0, start);
      const selection = text.substring(start, end);
      const after = text.substring(end);
      
      let insertText = '';
      switch (tag) {
        case 'bold':
          insertText = `**${selection}**`;
          break;
        case 'italic':
          insertText = `*${selection}*`;
          break;
        case 'link':
          insertText = `[${selection}](url)`;
          break;
        case 'list':
          insertText = `- ${selection}`;
          break;
        case 'heading':
          insertText = `# ${selection}`;
          break;
        case 'image':
          insertText = `![${selection}](image-url)`;
          break;
        default:
          insertText = selection;
      }
      
      const newText = before + insertText + after;
      setValue('content', newText);
      
      // Focus back to textarea
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start, start + insertText.length);
      }, 0);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Content Management</h1>
              <p className="text-gray-600 mt-2">Manage all static and dynamic site content</p>
            </div>
            <Button onClick={() => setShowModal(true)}>
              <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
              Add Content Section
            </Button>
          </div>
        </div>

        {/* Filters and Search */}
        <Card className="p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Types</option>
                {contentTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Page Location</label>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Locations</option>
                {pageLocations.map(location => (
                  <option key={location.value} value={location.value}>{location.label}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
                <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </Card>

        {/* Content Sections List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading content sections...</p>
          </div>
        ) : filteredSections.length === 0 ? (
          <Card className="p-12 text-center">
            <SafeIcon icon={FiFileText} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No content sections found</h3>
            <p className="text-gray-600 mb-4">Get started by creating your first content section.</p>
            <Button onClick={() => setShowModal(true)}>
              <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
              Add Content Section
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredSections.map((section) => {
              const typeInfo = getContentTypeInfo(section.content_type);
              const locationInfo = getPageLocationInfo(section.page_location);
              
              return (
                <Card key={section.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.color}`}>
                          <SafeIcon icon={typeInfo.icon} className="w-3 h-3 mr-1" />
                          {typeInfo.label}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {locationInfo.label}
                        </span>
                        {section.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <SafeIcon icon={FiCheck} className="w-3 h-3 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            <SafeIcon icon={FiEye} className="w-3 h-3 mr-1" />
                            Inactive
                          </span>
                        )}
                      </div>
                      
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{section.title}</h3>
                      <div className="prose prose-sm text-gray-600 mb-4 max-w-none">
                        <div 
                          className="line-clamp-3"
                          dangerouslySetInnerHTML={{ 
                            __html: section.content.replace(/\n/g, '<br>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>')
                          }}
                        />
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-500 space-x-4">
                        <span>Order: {section.display_order}</span>
                        <span>Last updated: {new Date(section.updated_at || section.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(section)}
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(section.id)}
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingSection ? 'Edit Content Section' : 'Add New Content Section'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Section Title *
                  </label>
                  <Input
                    {...register('title', { required: 'Title is required' })}
                    placeholder="Enter section title"
                    className={errors.title ? 'border-red-500' : ''}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Type *
                  </label>
                  <select
                    {...register('content_type', { required: 'Content type is required' })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.content_type ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select content type</option>
                    {contentTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  {errors.content_type && (
                    <p className="mt-1 text-sm text-red-600">{errors.content_type.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Location *
                  </label>
                  <select
                    {...register('page_location', { required: 'Page location is required' })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.page_location ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select page location</option>
                    {pageLocations.map(location => (
                      <option key={location.value} value={location.value}>{location.label}</option>
                    ))}
                  </select>
                  {errors.page_location && (
                    <p className="mt-1 text-sm text-red-600">{errors.page_location.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Order
                  </label>
                  <Input
                    type="number"
                    {...register('display_order')}
                    placeholder="0"
                    min="0"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value={true}
                        {...register('is_active')}
                        className="mr-2 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value={false}
                        {...register('is_active')}
                        className="mr-2 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Inactive</span>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Content *
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={toggleRichTextEditor}
                  >
                    {showRichTextEditor ? 'Hide' : 'Show'} Rich Text Tools
                  </Button>
                </div>
                
                {showRichTextEditor && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg border">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertRichTextTag('bold')}
                      >
                        <strong>B</strong>
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertRichTextTag('italic')}
                      >
                        <em>I</em>
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertRichTextTag('link')}
                      >
                        <SafeIcon icon={FiLink} className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertRichTextTag('list')}
                      >
                        List
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertRichTextTag('heading')}
                      >
                        H1
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertRichTextTag('image')}
                      >
                        <SafeIcon icon={FiImage} className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Select text and click a button to format, or click without selection to insert at cursor
                    </p>
                  </div>
                )}
                
                <textarea
                  {...register('content', { required: 'Content is required' })}
                  rows={12}
                  placeholder="Enter your content here. You can use Markdown formatting: **bold**, *italic*, [links](url), - lists, # headings, ![images](url)"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm ${
                    errors.content ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingSection ? 'Update Content Section' : 'Create Content Section'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ContentManagement;
